<template>
  <div :class="['data-import-module', {'is-inline': inline}]"
       v-loading="module.loading"
       element-loading-text="配置加载中，请稍等...">
    <div class="module-step" v-if="step === 'module'">
      <template v-if="module.config">
        <div class="module-file-download">
          <div class="module-tip">
            <div class="title">{{ title }}</div>
            <div class="instruction" v-if="module.config.instruction">
              <span>{{module.config.instruction}}</span>
            </div>
          </div>
          <div class="module-file">
            <action-form ref="downloadForm"
                         class="download-form"
                         :action="downloadTemplateUrl"
                         method="post"
                         target="_blank">
              <template v-if="module.uploadParams">
                <template v-for="(value, key) in module.uploadParams">
                  <template v-if="Array.isArray(value)">
                    <input v-for="(val, index) in value"
                           :key="'upload-param-' + key + '-' + index"
                           type="hidden"
                           :name="key"
                           :value="val"/>
                  </template>
                  <input v-else
                         :key="'upload-param-' + key"
                         type="hidden"
                         :name="key"
                         :value="value"/>
                </template>
              </template>
              <el-button type="default" @click="downloadTemplateFile">
                <span>{{ downloadText }}</span>
              </el-button>
            </action-form>
          </div>
        </div>
        <el-upload ref="dataImportModuleUpload"
                   :class="['data-import-file-upload', {'upload-ready': !!module.readyFile}]"
                   drag
                   :accept="supportFileType.length ? supportFileType.join(',') : ''"
                   :show-file-list="false"
                   :on-change="handleFileChange"
                   :before-upload="beforeFileUpload"
                   :on-progress="handleFileUploadProgress"
                   :on-success="handleFileUploadSuccess"
                   :on-error="handleFileUploadError"
                   name="uploadFile"
                   :http-request="options => httpRequest(options, true)"
                   :action="uploadFileUrl"
                   :auto-upload="false">
          <i class="el-upload__icon iconfont icon-file-excel-fill"></i>
          <template v-if="module.readyFile">
            <div class="el-upload__text">{{module.readyFile.name}}</div>
            <div class="el-upload__tip">
              {{module.readyFile.size | formatFileSize}}
            </div>
            <div class="el-upload__btn">
              <el-button type="default">重新选择</el-button>
            </div>
          </template>
          <template v-else>
            <div class="el-upload__text">将文件拖拽到此区域</div>
            <div class="el-upload__tip">
              只能上传<span class="upload-type">{{supportFileType.length ? supportFileType.join('、') : '未指定'}}</span>文件，且不超过{{maxUploadFileSize | formatFileSize}}
            </div>
            <div class="el-upload__btn">
              <el-button type="default">{{ uploadText }}</el-button>
            </div>
          </template>
        </el-upload>
      </template>
    </div>
    <div class="parser-step" v-else-if="step === 'parser'"
         v-loading="parser.loading"
         element-loading-text="数据加载中，请稍等...">
      <template v-if="parser.importData">
        <template v-if="state.importComplete && dataImport.resultMsg">
          <div class="parser-result">
            <div class="state-info">
              <i class="el-icon--state" :class="{true: 'el-icon-success', false: 'el-icon-warning'}[!dataImport.ignoreRows.length]"></i>
            </div>
            <div class="result-msg">
              <div class="content">{{dataImport.resultMsg}}</div>
              <div v-if="dataImport.ignoreRows.length"
                   class="ignore-tip">忽略{{dataImport.ignoreRows.length}}行表格数据</div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="parser-detail">
            <div class="upload-detail">
              <div class="upload-info">
                <a v-if="module.uploadFile.link"
                   class="theme-text-hover"
                   title="上传源文件"
                   :href="$contextPath(module.uploadFile.link)"
                   target="_blank">
                  <i class="el-icon-document"></i>
                </a>
                <span>本次共上传了<span class="row-count">{{uploadDataCount}}</span>行数据</span>
              </div>
              <el-dropdown class="ignore-info" v-if="dataImport.ignoreRows && dataImport.ignoreRows.length > 0">
                <span class="el-dropdown-link">
                  已忽略<span class="row-count">{{dataImport.ignoreRows.length}}</span>行数据<i class="el-icon-caret-bottom el-icon--right"></i>
                </span>
                <el-dropdown-menu class="data-import-module-ignore-dropdown" slot="dropdown">
                  <AppScrollbar style="height: 100%"
                                scroll-x>
                    <div class="drop-menu-item">
                      <el-dropdown-item v-for="(rowIndex, index) in dataImport.ignoreRows"
                                        :key="'ignore-row' + index"
                                        @click.native="scrollToRowIndex(rowIndex)">第{{(parser.importData.startRow || 1) + rowIndex + 1}}行</el-dropdown-item>
                    </div>
                  </AppScrollbar>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
            <div class="import-detail">
              <div class="import-detail-item" v-if="dataImport.importing && parser.importData.dataList && uploadDataCount > 0">
                <el-progress :text-inside="true" :stroke-width="16" :percentage="Math.round((dataImport.rowIndex + 1) * 100 / uploadDataCount)"></el-progress>
              </div>
            </div>
          </div>
          <div class="parser-data">
            <div class="parser-data-list">
              <RecordGrid ref="grid"
                          size="mini"
                          height="auto"
                          :border="false"
                          :fit="false"
                          :columns="uploadDataColumns"
                          :data="uploadDataList"
                          :row-class-name="getUploadRowClass"
                          highlight-current-row
                          highlight-hover-row
                          show-overflow="title"
                          show-header-overflow="title"
                          resizable
                          row-id="id"></RecordGrid>
            </div>
          </div>
        </template>
      </template>
      <template v-else-if="!parser.loading">
        <Hint v-if="parser.errorMsg"
              type="error"
              class="error-msg">
          <span>{{parser.errorMsg}}</span>
        </Hint>
      </template>
    </div>
    <div class="footer">
      <div v-if="$slots.external"
           class="external-group">
        <slot name="external"></slot>
      </div>
      <div class="internal-group">
        <div class="button-group">
          <template v-if="step === 'module'">
            <el-button type="primary" size="mini" :disabled="module.uploadFile.uploading" v-if="module.config" @click="startUploadFile">{{module.uploadFile.uploading ? `上传中...${module.uploadFile.percent}%` : '开始上传'}}</el-button>
          </template>
          <template v-else-if="step === 'parser'">
            <template v-if="dataImport.importing">
              <el-button type="danger" plain size="mini" :disabled="state.isInterrupting" v-if="dataImport.rowIndex < uploadDataCount - 1" @click="cancelDataImport">取消数据导入</el-button>
            </template>
            <template v-else>
              <el-button type="warning" plain size="mini" @click="step = 'module'">重新{{ uploadText }}</el-button>
              <template v-if="state.importComplete">
                <a class="el-button el-button--info el-button--mini"
                   v-if="dataImport.errorLink"
                   :href="$contextPath(dataImport.errorLink)"
                   target="_blank">
                  <span>下载失败列表</span>
                </a>
              </template>
              <template v-else>
                <el-button type="primary" size="mini" v-if="parser.importData" @click="confirmImportData">{{state.isImported ? '重新导入数据' : '确认导入数据'}}</el-button>
              </template>
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Support from '../../mixins/support'
import ElUpload from '../../mixins/upload/ElUpload'
import AppScrollbar from '../elements/AppScrollbar'
import RecordGrid from './RecordGrid'
import Hint from './Hint'
export default {
  mixins: [Support, ElUpload],
  name: 'DataImportModule',
  components: {
    AppScrollbar,
    RecordGrid,
    Hint
  },
  props: {
    title: {
      type: String,
      default: '请下载模版文件，按格式修改后导入'
    },
    downloadText: {
      type: String,
      default: '下载模板'
    },
    uploadText: {
      type: String,
      default: '上传文件'
    },
    importUrl: {
      required: true,
      type: String
    },
    preHandleParams: {
      type: Function
    },
    extraParams: {
      type: Object,
      required: false
    },
    preHandleVerify: {
      type: Function
    },
    inline: Boolean
  },
  data () {
    return {
      isAlive: true,
      pollDelay: 500,
      step: 'module',
      module: {
        uploadParams: null,
        loading: false,
        config: null,
        readyFile: null,
        uploadFile: {
          uploading: false,
          percent: 0,
          link: null,
          defaultMaxSize: 100 * 1024 * 1024
        }
      },
      parser: {
        loading: false,
        importData: null,
        errorMsg: ''
      },
      dataImport: {
        importing: false,
        rowIndex: -1,
        ignoreRows: [],
        errorInfos: [],
        resultMsg: null,
        errorLink: null
      },
      state: {
        isInterrupting: false,
        isImported: false,
        importComplete: false
      }
    }
  },
  computed: {
    downloadTemplateUrl: function () {
      return this.$contextPath(this.getActionUrl('Template'))
    },
    supportFileType: function () {
      let config = this.module.config
      if (config) {
        return config.supportFileType || []
      }
      return []
    },
    maxUploadFileSize: function () {
      let config = this.module.config
      if (config) {
        return config.maxFileSize || this.module.uploadFile.defaultMaxSize
      }
      return this.module.uploadFile.defaultMaxSize
    },
    uploadFileUrl: function () {
      let query = this.$qs.stringify(this.getActionParams(), {
        arrayFormat: 'repeat'
      })
      let uploadUrl = this.$contextPath(this.getActionUrl('Upload'))
      if (query) {
        if (uploadUrl.includes('?')) {
          return uploadUrl + '&' + query
        } else {
          return uploadUrl + '?' + query
        }
      } else {
        return uploadUrl
      }
    },
    uploadDataCount: function () {
      if (this.parser.importData && this.parser.importData.dataList) {
        return this.parser.importData.dataList.length
      } else {
        return 0
      }
    },
    uploadDataColumns: function () {
      if (this.parser.importData && this.parser.importData.headerList) {
        let headerList = this.parser.importData.headerList.map((header, index) => {
          return {
            field: `${index}`,
            title: header.title + (header.isAllowBlank ? '' : ' *'),
            minWidth: 100
          }
        })
        if (this.dataImport.errorInfos.length) {
          headerList.push({
            title: '错误信息',
            minWidth: 200,
            fixed: 'right',
            slots: {
              default: ({rowIndex}, h) => {
                if (this.dataImport.rowIndex >= rowIndex) {
                  let getErrRow = this.dataImport.ignoreRows.indexOf(rowIndex)
                  if (getErrRow >= 0) {
                    return [h('span', this.dataImport.errorInfos[getErrRow])]
                  } else {
                    return [h('i', {class: 'el-icon-check'})]
                  }
                }
              }
            }
          })
        }
        return headerList
      }
    },
    uploadDataList: function () {
      if (this.parser.importData && this.parser.importData.dataList) {
        return this.parser.importData.dataList
      } else {
        return []
      }
    }
  },
  methods: {
    getActionUrl (action) {
      let importUrl = this.importUrl
      let quotIndex = importUrl.indexOf('?')
      if (quotIndex > 0) {
        return `${importUrl.substring(0, quotIndex)}${action}${importUrl.substring(quotIndex)}`
      } else {
        return `${importUrl}${action}`
      }
    },
    getActionParams () {
      if (this.preHandleParams) {
        return this.$extend(this.preHandleParams(), this.extraParams)
      } else {
        return this.extraParams || {}
      }
    },
    downloadTemplateFile () {
      let preHandleVerify = this.preHandleVerify
      if (preHandleVerify && !preHandleVerify()) {
        return
      }
      let downloadForm = this.$refs['downloadForm']
      if (downloadForm) {
        this.module.uploadParams = this.$extend(this.getActionParams(), this.$attrs)
        this.$nextTick(() => {
          downloadForm.submit()
        })
      }
    },
    // 校验文件
    validateFile (file, showMsg) {
      let filename = file.name
      let index = filename.lastIndexOf('.')
      if (index > 0) {
        let suffix = filename.substring(index).toLowerCase()
        if (!this.supportFileType.includes(suffix)) {
          if (showMsg) {
            this.$message.error('上传的文件格式不合法')
          }
          return false
        }
      } else {
        if (showMsg) {
          this.$message.error('上传的文件格式不合法')
        }
        return false
      }
      let isLt = file.size <= this.maxUploadFileSize
      if (isLt) {
        return true
      } else {
        if (showMsg) {
          this.$message.error('上传的文件大小超过限制')
        }
        return false
      }
    },
    // 文件准备
    handleFileChange (file, fileList) {
      if (file.status === 'ready') {
        if (this.validateFile(file, true)) {
          this.module.readyFile = file
          let dataImportModuleUpload = this.$refs['dataImportModuleUpload']
          if (dataImportModuleUpload) {
            fileList.forEach(f => {
              if (f !== file) {
                dataImportModuleUpload.handleRemove(f)
              }
            })
          }
        } else {
          this.resetModuleData()
        }
      }
    },
    // 上传前验证
    beforeFileUpload (file) {
      if (this.validateFile(file, true)) {
        this.module.uploadFile.uploading = true
        this.module.uploadFile.percent = 0
        return true
      } else {
        return false
      }
    },
    // 上传进度
    handleFileUploadProgress (event) {
      this.module.uploadFile.percent = Math.round(event.percent)
    },
    // 上传成功
    handleFileUploadSuccess (res) {
      this.module.uploadFile.uploading = false
      if (res['responseCode'] === 'SUCCESS') {
        this.module.uploadFile.link = res['fileUrl']
        this.step = 'parser'
      } else {
        this.$message.error(res.message || '获取返回信息失败')
      }
    },
    // 上传错误
    handleFileUploadError () {
      this.module.uploadFile.uploading = false
      this.$message.error('文件上传失败')
      this.$nextTick(() => this.resetModuleData())
    },
    // 上传文件
    startUploadFile () {
      let preHandleVerify = this.preHandleVerify
      if (preHandleVerify && !preHandleVerify()) {
        return
      }
      if (!this.module.readyFile) {
        this.$message.warning(`请先选择${this.uploadText}`)
        return
      }
      let dataImportModuleUpload = this.$refs['dataImportModuleUpload']
      if (dataImportModuleUpload) {
        dataImportModuleUpload.submit()
      } else {
        this.$notify({
          title: '操作错误',
          message: '请等待组件初始化完毕',
          type: 'error'
        })
      }
    },
    // 行样式
    getUploadRowClass ({rowIndex}) {
      if (this.dataImport.rowIndex >= rowIndex) {
        if (this.dataImport.ignoreRows.includes(rowIndex)) {
          return 'failure-row'
        } else {
          return 'success-row'
        }
      }
    },
    // 加载导入配置
    loadImportConfigData () {
      this.module.loading = true
      let params = this.$extend(this.getActionParams(), this.$attrs)
      this.$ajax({
        url: this.importUrl,
        method: 'post',
        data: this.$qs.stringify(params, {
          arrayFormat: 'repeat'
        })
      }).then((response) => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.module.config = response.data['config']
              // 是否包含场景信息
              let taskSceneData = response.data['taskSceneData']
              if (taskSceneData) {
                this.$confirm('当前任务正在进行，是否进入?', '提示', {
                  confirmButtonText: '确定',
                  cancelButtonText: '取消',
                  type: 'warning'
                }).then(() => {
                  this.module.uploadFile.link = taskSceneData['fileUrl']
                  this.parser.importData = taskSceneData['parserData']
                  this.step = 'parser'
                  this.dataImport.importing = true
                  // 进入并查看状态
                  this.loadDataImportState()
                }).catch(() => {})
              }
              break
            }
            default: {
              this.$message({
                message: response.data['message'],
                type: 'warning',
                center: true
              })
              break
            }
          }
        }
      }, () => {
        this.$message.error({
          message: '请求发送失败，请稍候重试',
          center: true
        })
      }).finally(() => {
        this.module.loading = false
      })
    },
    // 加载解析数据
    loadImportParserData () {
      this.parser.loading = true
      let params = this.$extend({
        parserPath: this.module.uploadFile.link
      }, this.getActionParams(), this.$attrs)
      this.$ajax({
        url: this.getActionUrl('Parser'),
        method: 'post',
        data: this.$qs.stringify(params, {
          arrayFormat: 'repeat'
        })
      }).then((response) => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.parser.importData = response.data['parserData']
              break
            }
            default: {
              let errorMsg = response.data['message'] || '解析文件数据失败'
              this.$message({
                message: errorMsg,
                type: 'warning',
                center: true
              })
              this.parser.errorMsg = errorMsg
              break
            }
          }
        }
      }, () => {
        let errorMsg = '请求发送失败，请稍候重试'
        this.$message.error({
          message: errorMsg,
          center: true
        })
        this.parser.errorMsg = errorMsg
      }).finally(() => {
        this.parser.loading = false
      })
    },
    // 确认导入数据
    confirmImportData () {
      this.resetImportState()
      this.dataImport.importing = true
      this.state.isImported = true
      let params = this.$extend({
        parserPath: this.module.uploadFile.link
      }, this.getActionParams(), this.$attrs)
      this.$ajax({
        url: this.getActionUrl('Import'),
        method: 'post',
        data: this.$qs.stringify(params, {
          arrayFormat: 'repeat'
        })
      }).then((response) => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.loadDataImportState()
              break
            }
            default: {
              this.$message({
                message: response.data['message'],
                type: 'warning',
                center: true
              })
              this.dataImport.importing = false
              break
            }
          }
        }
      }, () => {
        this.$message.error({
          message: '请求发送失败，请稍候重试',
          center: true
        })
        this.dataImport.importing = false
      })
    },
    // 获取任务执行状态
    loadDataImportState () {
      if (this.isAlive) {
        let params = this.$extend(this.getActionParams(), this.$attrs)
        this.$ajax({
          url: this.getActionUrl('State'),
          method: 'post',
          data: this.$qs.stringify(params, {
            arrayFormat: 'repeat'
          })
        }).then((response) => {
          if (response.data) {
            let taskState = response.data['taskState']
            if (taskState && taskState['state']) {
              // 刷新执行行数
              let stateInfo = taskState['stateInfo']
              if (stateInfo) {
                let rowIndex = stateInfo['rowIndex']
                if (typeof rowIndex === 'number') {
                  this.dataImport.rowIndex = rowIndex
                }
                let ignoreRows = stateInfo['ignoreRows']
                if (ignoreRows) {
                  this.dataImport.ignoreRows = ignoreRows
                }
                let errorInfos = stateInfo['errorInfos']
                if (errorInfos) {
                  this.dataImport.errorInfos = errorInfos
                }
              }
              switch (taskState['state']) {
                case 'PREPARING':
                  // 准备状态
                  break
                case 'RUNNING':
                  // 运行状态
                  break
                case 'IGNORE_AWAIT':
                  this.$confirm(taskState['message'], '提示', {
                    confirmButtonText: '忽略全部',
                    cancelButtonText: '忽略本条',
                    type: 'warning',
                    closeOnClickModal: false,
                    distinguishCancelAndClose: true,
                    callback: action => {
                      switch (action) {
                        case 'confirm':
                          this.submitDataImportReply(true, true)
                          break
                        case 'cancel':
                          this.submitDataImportReply(true, false)
                          break
                        case 'close':
                          this.submitDataImportReply(false)
                          break
                      }
                    }
                  })
                  return
                case 'AWAIT':
                  this.$confirm(taskState['message'], '提示', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning',
                    closeOnClickModal: false
                  }).then(() => {
                    this.submitDataImportReply(true)
                  }).catch(() => {
                    this.submitDataImportReply(false)
                  })
                  return
                case 'INTERRUPT':
                  this.$alert(taskState['message'], '操作中断', {
                    confirmButtonText: '确定',
                    type: 'warning',
                    callback: () => {
                      this.dataImport.importing = false
                    }
                  })
                  return
                case 'COMPLETE':
                  let confirmType = taskState['confirmType'] || 'success'
                  if (stateInfo) {
                    let errorLink = stateInfo['errorLink']
                    if (errorLink) {
                      this.dataImport.errorLink = errorLink
                    }
                  }
                  let resultMsg = taskState['message']
                  if (confirmType === 'success') {
                    this.dataImport.resultMsg = resultMsg
                    this.$emit('dataImportSuccess')
                  } else {
                    this.$alert(resultMsg, '执行结束', {
                      confirmButtonText: '确定',
                      type: confirmType
                    })
                  }
                  this.state.importComplete = true
                  this.dataImport.importing = false
                  return
                default:
                  return
              }
            } else {
              this.$alert('导入过程执行结束', '执行结束', {
                confirmButtonText: '确定',
                type: 'success',
                callback: () => {
                  this.dataImport.importing = false
                }
              })
            }
          }
          this.$nextTick(() => {
            setTimeout(() => {
              this.loadDataImportState()
            }, this.pollDelay)
          })
        }, () => {
          setTimeout(() => {
            this.loadDataImportState()
          }, this.pollDelay)
        })
      }
    },
    // 提交回复信息
    submitDataImportReply (isOk, isAll) {
      let params = this.$extend({
        replyType: isOk,
        ignoreAll: isAll
      }, this.getActionParams(), this.$attrs)
      this.$ajax({
        url: this.getActionUrl('Reply'),
        method: 'post',
        data: this.$qs.stringify(params, {
          arrayFormat: 'repeat'
        })
      }).then((response) => {
        // 无需处理结果
      }, () => {
        this.$message.error({
          message: '请求发送失败，请稍候重试',
          center: true
        })
      }).finally(() => {
        this.loadDataImportState()
      })
    },
    // 取消数据导入
    cancelDataImport () {
      this.state.isInterrupting = true
      let params = this.$extend(this.getActionParams(), this.$attrs)
      this.$ajax({
        url: this.getActionUrl('Interrupt'),
        method: 'post',
        data: this.$qs.stringify(params, {
          arrayFormat: 'repeat'
        })
      }).then((response) => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              // 不做处理
              break
            }
            default: {
              this.$message({
                message: response.data['message'],
                type: 'warning',
                center: true
              })
              break
            }
          }
        }
      }, () => {
        this.$message.error({
          message: '请求发送失败，请稍候重试',
          center: true
        })
      }).finally(() => {
        this.state.isInterrupting = false
      })
    },
    // 重置module数据
    resetModuleData () {
      this.module.readyFile = null
      this.module.uploadFile.link = null
      let dataImportModuleUpload = this.$refs['dataImportModuleUpload']
      if (dataImportModuleUpload) {
        dataImportModuleUpload.clearFiles()
      }
    },
    // 重置导入状态
    resetImportState () {
      this.dataImport.rowIndex = -1
      this.dataImport.ignoreRows = []
      this.dataImport.errorInfos = []
      this.dataImport.resultMsg = null
      this.dataImport.errorLink = null
      let grid = this.$refs['grid']
      if (grid) {
        grid.scrollTo(null, 0)
      }
    },
    // 重置parser数据
    resetParserData () {
      this.parser.importData = null
      this.parser.errorMsg = ''
      this.dataImport.rowIndex = -1
      this.dataImport.ignoreRows = []
      this.dataImport.errorInfos = []
      this.dataImport.resultMsg = null
      this.dataImport.errorLink = null
      this.state.isImported = false
      this.state.importComplete = false
    },
    // 滑动到指定行
    scrollToRowIndex (rowIndex) {
      if (this.uploadDataCount > 0) {
        let grid = this.$refs['grid']
        if (grid) {
          grid.scrollToRow(this.uploadDataList[rowIndex], null)
        }
      }
    }
  },
  mounted () {
    this.loadImportConfigData()
  },
  destroyed () {
    this.isAlive = false
  },
  watch: {
    step: function (newValue) {
      if (newValue) {
        switch (newValue) {
          case 'module': {
            this.resetModuleData()
            break
          }
          case 'parser': {
            this.resetParserData()
            this.loadImportParserData()
            break
          }
        }
      }
    },
    'dataImport.rowIndex': function (newValue) {
      this.scrollToRowIndex(newValue)
    }
  }
}
</script>

<style lang="scss">
  @use "../../styles/color" as *;

  .data-import-module {
    height: 450px;
    display: flex;
    flex-direction: column;
    .module-step {
      flex: auto;
      display: flex;
      flex-direction: column;
      padding: 0 15px;
      box-sizing: border-box;
      &>.module-file-download {
        flex: none;
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: #fff3e9;
        border: 1px solid #fdd5b0;
        padding: 16px 20px;
        margin-top: 15px;
        &>.module-tip {
          &>.title {
            color: #e17655;
            font-size: 14px;
            font-weight: bold;
          }
          &>.instruction {
            margin-top: 5px;
            color: #606266;
            font-size: 12px;
          }
        }
        &>.module-file {
          margin-left: 18px;
          color: #909399;
          .download-form {
            .el-button {
              padding: 7px 16px;
              border-radius: 0;
            }
          }
        }
      }
      &>.data-import-file-upload {
        flex: auto;
        display: flex;
        flex-direction: column;
        margin-top: 15px;
        &.upload-ready {
          .el-upload {
            .el-upload-dragger {
              .el-upload__icon {
                color: #61af44;
              }
            }
          }
        }
        .el-upload {
          flex: auto;
          display: flex;
          flex-direction: column;
          .el-upload-dragger {
            flex: auto;
            width: 100%;
            min-height: 260px;
            border-radius: 0;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            .el-upload__icon {
              margin-top: -10px;
              color: #dde5eb;
              font-size: 60px;
              width: auto;
            }
            .el-upload__text {
              font-size: 12px;
              line-height: 24px;
            }
            .el-upload__tip {
              margin-top: 8px;
              color: #909399;
              line-height: 20px;
              .upload-type {
                margin: 0 2px;
                color: $set-primary-color;
              }
            }
            .el-upload__btn {
              margin-top: 15px;
              .el-button {
                padding: 6px 12px;
                border-radius: 0;
              }
            }
          }
        }
      }
    }
    .parser-step {
      flex: auto;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      &>.parser-result {
        flex: auto;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        margin: 15px 15px 0;
        background-color: #fff;
        overflow: hidden;
        &>.state-info {
          .el-icon--state {
            font-size: 54px;
            &.el-icon-success {
              color: $set-success-color;
            }
            &.el-icon-warning {
              color: $set-warning-color;
            }
          }
        }
        &>.result-msg {
          align-self: stretch;
          overflow: auto;
          margin-top: 15px;
          font-size: 14px;
          text-align: center;
          &>.content {
            white-space: pre-wrap;
          }
          &>.ignore-tip {
            margin-top: 8px;
            color: #909399;
            font-size: 12px;
          }
        }
      }
      &>.parser-detail {
        flex: none;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 12px;
        padding: 10px 15px;
        &>.upload-detail {
          display: flex;
          align-items: center;
          &>.upload-info {
            a {
              margin-right: 2px;
              color: inherit;
              font-size: 13px;
              text-decoration: none;
              vertical-align: -1px;
            }
            .row-count {
              margin: 0 2px;
              color: $set-primary-color;
            }
            &~.ignore-info {
              position: relative;
              padding-left: 10px;
              &:before {
                content: '';
                position: absolute;
                left: 0;
                top: 50%;
                width: 1px;
                height: 10px;
                margin-top: -4px;
                background-color: #c0c4cc;
              }
            }
          }
          &>.ignore-info {
            margin-left: 10px;
            font-size: 12px;
            color: #e6a23c;
            vertical-align: 1px;
            cursor: pointer;
            .row-count {
              margin: 0 2px;
            }
          }
        }
        &>.import-detail {
          .import-detail-item {
            display: flex;
            align-items: center;
            .el-progress {
              &.el-progress--line {
                width: 150px;
                &>.el-progress-bar {
                  &>.el-progress-bar__outer {
                    border-radius: 0;
                    background-color: transparent;
                    &:after {
                      content: '';
                      position: absolute;
                      left: 0;
                      bottom: 0;
                      width: 100%;
                      height: 3px;
                      background-color: #ebeef5;
                    }
                    &>.el-progress-bar__inner {
                      border-radius: 0;
                      background-color: transparent;
                      &:after {
                        content: '';
                        position: absolute;
                        left: 0;
                        bottom: 0;
                        width: 100%;
                        height: 3px;
                        background-color: $set-primary-color;
                        z-index: 1;
                      }
                      &>.el-progress-bar__innerText {
                        margin: 0;
                        color: #303133;
                        transform: scale(.9) translate(5%, -30%);
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      &>.parser-data {
        flex: auto;
        position: relative;
        &>.parser-data-list {
          position: absolute;
          left: 0;
          top: 0;
          right: 0;
          bottom: 0;
          .vxe-grid {
            &>.vxe-table {
              .vxe-body--row {
                &.success-row {
                  color: $set-success-color;
                  background-color: $set-success-lighter-color!important;
                }
                &.failure-row {
                  color: $set-danger-color;
                  background-color: $set-danger-lighter-color!important;
                }
              }
              .vxe-table--fixed-left-wrapper {
                box-shadow: none;
                &.scrolling--middle {
                  border-right: 1px solid rgba(0,0,0,.05);
                }
              }
              .vxe-table--fixed-right-wrapper {
                &.scrolling--middle {
                  border-left: 1px solid rgba(0,0,0,.05);
                  box-shadow: -2px 0 2px rgba(0,0,0,.05);
                }
              }
            }
          }
        }
      }
      &>.error-msg {
        flex: auto;
        margin: 15px 15px 0;
        background-color: #fff;
      }
    }
    .footer {
      flex: none;
      display: flex;
      align-items: center;
      padding: 15px;
      &>.external-group {
        flex: none;
        margin-right: 10px;
        &~.internal-group {
          &>.button-group {
            text-align: right;
          }
        }
      }
      &>.internal-group {
        flex: auto;
        &>.button-group {
          text-align: center;
          .el-button {
            text-decoration: none;
            &+.el-button {
              margin-left: 8px;
            }
          }
        }
      }
    }
    &.is-inline {
      .module-step {
        padding-left: 0;
        padding-right: 0;
        &>.module-file-download {
          margin-top: 0;
        }
      }
      .parser-step {
        &>.parser-detail {
          padding-left: 0;
          padding-right: 0;
        }
        &>.parser-result,
        &>.error-msg {
          margin-left: 0;
          margin-right: 0;
          border: 1px solid #ebeef5;
        }
      }
      .footer {
        padding-left: 0;
        padding-right: 0;
      }
    }
  }
  .data-import-module-ignore-dropdown {
    min-width: 90px;
    .drop-menu-item {
      max-height: 200px;
      .el-dropdown-menu__item {
        font-size: 12px;
        line-height: 26px;
      }
    }
  }
</style>
